import { strToU8, zipSync } from 'fflate';
import type { L3Report } from './l3-report';
import type { SmokeResult } from './types';
import { OpfsWorkspace } from './workspace';

type EvidenceEntry = {
  name: string;
  mimeType: string;
  data: Uint8Array;
};

type EvidenceManifest = {
  schemaVersion: 1;
  createdAt: string;
  commit: string;
  treeState: string;
  origin: string;
  entries: Array<{ name: string; mimeType: string; sizeBytes: number; sha256: string }>;
};

function jsonEntry(name: string, value: unknown): EvidenceEntry {
  return { name, mimeType: 'application/json', data: strToU8(`${JSON.stringify(value, null, 2)}\n`) };
}

async function sha256(data: Uint8Array): Promise<string> {
  const copy = Uint8Array.from(data);
  const digest = await crypto.subtle.digest('SHA-256', copy);
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, '0')).join('');
}

async function fileEntry(name: string, file: File): Promise<EvidenceEntry> {
  return { name, mimeType: file.type || 'application/octet-stream', data: new Uint8Array(await file.arrayBuffer()) };
}

export function evidenceItemCount(smoke: SmokeResult | null, l3: L3Report | null): number {
  return (smoke ? 1 + (smoke.artifact ? 1 : 0) : 0) + (l3 ? 1 : 0);
}

export async function createEvidenceArchive(smoke: SmokeResult | null, l3: L3Report | null): Promise<File | null> {
  const entries = new Map<string, EvidenceEntry>();
  const add = (entry: EvidenceEntry): void => {
    if (!entries.has(entry.name)) entries.set(entry.name, entry);
  };
  try {
    const workspace = await OpfsWorkspace.open();
    const [reports, artifacts] = await Promise.all([workspace.listReports(), workspace.listReadyArtifacts()]);
    for (const report of reports) add(await fileEntry(`reports/${report.runId}.json`, report.file));
    for (const artifact of artifacts) add(await fileEntry(`exports/${artifact.exportId}/file.webm`, artifact.file));
  } catch {
    // A report generated before OPFS support can still be archived from this session.
  }
  if (smoke) {
    add(jsonEntry(`reports/${smoke.report.runId}.json`, smoke.report));
    if (smoke.artifact) add(await fileEntry(`exports/${smoke.report.runId}/file.webm`, smoke.artifact));
  }
  if (l3) add(jsonEntry(`reports/${l3.runId}.json`, l3));
  if (entries.size === 0) return null;

  const report = smoke?.report ?? l3;
  if (!report) return null;
  const manifest: EvidenceManifest = {
    schemaVersion: 1,
    createdAt: new Date().toISOString(),
    commit: report.environment.commit,
    treeState: report.environment.treeState,
    origin: report.environment.origin,
    entries: await Promise.all(Array.from(entries.values()).map(async (entry) => ({
      name: entry.name,
      mimeType: entry.mimeType,
      sizeBytes: entry.data.byteLength,
      sha256: await sha256(entry.data),
    }))),
  };
  const archive = zipSync({
    'manifest.json': strToU8(`${JSON.stringify(manifest, null, 2)}\n`),
    ...Object.fromEntries(Array.from(entries.values(), (entry) => [entry.name, entry.data])),
  }, { level: 6 });
  return new File([archive], `bitpop-evidence-${new Date().toISOString().replaceAll(/[:.]/g, '-')}.zip`, { type: 'application/zip' });
}
